import { useCallback } from 'react';
import { doc, getDoc, updateDoc, UpdateData, DocumentData } from 'firebase/firestore';
import { db } from '../../../firebase';
import { FullProject, ProjectMetadata } from '../../../types';
import { areProjectIdsMatching } from '../../../utils/idNormalizer';

export interface UseProjectMetadataActionsProps {
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  markProjectDirty: (id: string) => void;
  INBOX_PROJECT_ID: string;
  COMM_PROJECT_ID: string;
  tenantId: string;
  normalizeProjectId: (id?: string) => string | undefined;
}

export function useProjectMetadataActions({
  allProjectsRef,
  setAllProjects,
  currentProjectId,
  setProjectMetadata,
  markProjectDirty,
  INBOX_PROJECT_ID,
  COMM_PROJECT_ID,
  tenantId,
  normalizeProjectId,
}: UseProjectMetadataActionsProps) {
  const updateProjectMetadata = useCallback((projectId: string, updates: Partial<ProjectMetadata>, forceGlobal?: boolean) => {
    const targetId = projectId === 'inbox_inforum' ? INBOX_PROJECT_ID : (projectId === 'comm_internal' ? COMM_PROJECT_ID : projectId);
    const normalizedTargetId = normalizeProjectId(targetId);

    if (projectId === 'inbox_inforum' || targetId === INBOX_PROJECT_ID) {
      // Route global keys (column styling) to the tenant settings document
      const globalKeys = ['categories_professional', 'customColumns', 'kanbanColumnStyles'];
      const isGlobal = forceGlobal !== undefined ? forceGlobal : Object.keys(updates).some(k => globalKeys.includes(k));
      const rawDocId = isGlobal ? 'inbox_inforum_' + tenantId : INBOX_PROJECT_ID;

      const updatedProjects = allProjectsRef.current.map(p => {
        if (p.id === rawDocId || p.rawId === rawDocId || (p.id === 'inbox_inforum' && rawDocId.startsWith('inbox_inforum'))) {
          return {
            ...p,
            metadata: { ...(p.metadata || {}), ...updates },
            lastModified: Date.now()
          };
        }
        return p;
      });
      allProjectsRef.current = updatedProjects;
      setAllProjects(updatedProjects);

      // Optimistic Sync if it's the current project
      if (currentProjectId === 'inbox_inforum') {
        setProjectMetadata(prev => ({ ...prev, ...updates }));
      }

      const partialUpdate: UpdateData<DocumentData> = {};
      Object.entries(updates).forEach(([key, value]) => {
        partialUpdate[`metadata.${key}`] = value;
      });
      if (Object.keys(partialUpdate).length > 0) {
        const docRef = doc(db, 'projects', rawDocId);
        updateDoc(docRef, partialUpdate).catch(err =>
          console.warn('[Metadata] Direct Firestore metadata update failed:', err)
        );
      }
      markProjectDirty(rawDocId);
      return;
    }

    const updatedProjects = allProjectsRef.current.map(p => {
      if (p.id === targetId || p.id === normalizedTargetId) {
        return {
          ...p,
          metadata: { ...(p.metadata || {}), ...updates },
          lastModified: Date.now()
        };
      }
      return p;
    });
    allProjectsRef.current = updatedProjects;
    setAllProjects(updatedProjects);

    if (areProjectIdsMatching(targetId, currentProjectId)) {
      setProjectMetadata(prev => ({ ...prev, ...updates }));
    }

    markProjectDirty(targetId);
  }, [currentProjectId, markProjectDirty, INBOX_PROJECT_ID, COMM_PROJECT_ID, tenantId, normalizeProjectId, setAllProjects, setProjectMetadata, allProjectsRef]);

  const updateProjectCategory = useCallback((projectId: string, categoryId: string | undefined) => {
    setAllProjects(prev => {
      const inboxIdx = prev.findIndex(p => p.id === 'inbox_inforum');
      if (inboxIdx === -1) return prev;
      
      const inboxProj = prev[inboxIdx];
      const oldCats = inboxProj.metadata?.projectCategories || {};
      const updatedCats = { ...oldCats };
      if (categoryId === undefined) {
        delete updatedCats[projectId];
      } else {
        updatedCats[projectId] = categoryId;
      }
      
      const updatedInbox = {
        ...inboxProj,
        metadata: {
          ...inboxProj.metadata,
          projectCategories: updatedCats
        },
        lastModified: Date.now()
      };
      
      const next = [...prev];
      next[inboxIdx] = updatedInbox;
      return next;
    });

    const inboxDocRef = doc(db, 'projects', INBOX_PROJECT_ID);
    getDoc(inboxDocRef).then((docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const currentMeta = data.metadata || {};
        const oldCats = currentMeta.projectCategories || {};
        const updatedCats = { ...oldCats };
        if (categoryId === undefined) {
          delete updatedCats[projectId];
        } else {
          updatedCats[projectId] = categoryId;
        }
        updateDoc(inboxDocRef, {
          'metadata.projectCategories': updatedCats
        }).catch(err => console.warn('[Metadata] Failed to save project category mapping:', err));
      }
    });
  }, [INBOX_PROJECT_ID, setAllProjects]);

  return {
    updateProjectMetadata,
    updateProjectCategory,
  };
}
